/**
 * Create and edit one shift.
 *
 * The same drawer serves both, because the fields are identical and a separate
 * "new shift" dialog is how the two drift apart. The mode only changes the
 * title, the primary button and whether Delete is offered.
 *
 * This is also where an availability conflict is resolved. A drop onto a day
 * somebody marked unavailable lands here with the conflict stated in plain
 * words; the boss can then roster it anyway, which sends `force: true`. That is
 * the whole reason the grid does not simply refuse the drop.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, CalendarOff, Lock, Trash2, Unlock } from 'lucide-react';
import type { Area, Shift, StaffMember } from '../lib/api.ts';
import { fmtDateLong, fmtDuration, fmtMoney, fmtTime12, shiftMinutes, type Hhmm, type IsoDate } from '../lib/time.ts';
import { Drawer } from '../components/Overlay.tsx';
import { Field, Spinner } from '../components/Bits.tsx';
import { bucketForDate, type Rates } from './rosterMath.ts';

export interface ShiftForm {
  id: string | null;
  date: IsoDate;
  startTime: Hhmm;
  endTime: Hhmm;
  userId: string | null;
  areaId: string | null;
  breakMinutes: number;
  notes: string;
  locked: boolean;
  status: 'draft' | 'published';
}

export function shiftToForm(s: Shift): ShiftForm {
  return {
    id: s.id,
    date: s.date,
    startTime: s.startTime.slice(0, 5),
    endTime: s.endTime.slice(0, 5),
    userId: s.userId,
    areaId: s.areaId,
    breakMinutes: s.breakMinutes,
    notes: s.notes ?? '',
    locked: s.locked,
    status: s.status,
  };
}

const BUCKET_LABEL: Record<string, string> = {
  ordinary: 'Weekday rate',
  saturday: 'Saturday rate',
  sunday: 'Sunday rate',
};

export function ShiftDrawer({
  open,
  form,
  staff,
  areas,
  rates,
  conflict,
  coverPanel,
  saving,
  deleting,
  isOwner,
  onClose,
  onSave,
  onDelete,
}: {
  open: boolean;
  form: ShiftForm | null;
  staff: StaffMember[];
  areas: Area[];
  rates: Record<string, Rates>;
  /** True when the assignee has marked this date unavailable. */
  conflict: { note: string | null; name: string } | null;
  /** Cover state for this shift, when it has any. Built by the roster page so
   *  this drawer stays a form and does not grow its own data fetching. */
  coverPanel?: ReactNode;
  saving: boolean;
  deleting: boolean;
  isOwner: boolean;
  onClose: () => void;
  onSave: (values: ShiftForm, force: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const [values, setValues] = useState<ShiftForm | null>(form);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setValues(form);
    setError(null);
    setConfirmDelete(false);
  }, [form]);

  if (!values) {
    return (
      <Drawer open={open} onClose={onClose} title="Shift">
        <div />
      </Drawer>
    );
  }

  const isNew = values.id === null;
  const minutes = shiftMinutes(values.startTime, values.endTime, values.breakMinutes);
  const assignee = values.userId ? staff.find((s) => s.userId === values.userId) : null;
  const bucket = bucketForDate(values.date);
  const rate = values.userId ? rates[values.userId]?.[bucket] : undefined;
  const cost = rate ? Math.round((minutes / 60) * rate * 100) / 100 : null;

  const set = <K extends keyof ShiftForm>(key: K, v: ShiftForm[K]) =>
    setValues((prev) => (prev ? { ...prev, [key]: v } : prev));

  function submit(force: boolean) {
    if (!values) return;
    if (minutes <= 0) {
      setError('The finish time has to be after the start time.');
      return;
    }
    if (values.breakMinutes < 0) {
      setError('A break cannot be negative.');
      return;
    }
    setError(null);
    onSave(values, force);
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isNew ? 'New shift' : 'Edit shift'}
      subtitle={fmtDateLong(values.date)}
      footer={
        <>
          {!isNew && values.id ? (
            <button
              type="button"
              className={confirmDelete ? 'btn btn-danger mr-auto' : 'btn mr-auto'}
              disabled={deleting || (values.locked && !isOwner)}
              onClick={() => (confirmDelete ? onDelete(values.id as string) : setConfirmDelete(true))}
              data-tip={
                values.locked && !isOwner
                  ? 'Locked|Only the owner can delete a locked shift.'
                  : 'Delete this shift|Click once to arm, again to confirm.'
              }
            >
              {deleting ? <Spinner size={15} label="Deleting" /> : <Trash2 size={15} aria-hidden="true" />}
              {confirmDelete ? 'Confirm delete' : 'Delete'}
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => submit(conflict !== null)} disabled={saving}>
            {saving ? <Spinner size={15} label="Saving" /> : null}
            {isNew ? (conflict ? 'Roster anyway' : 'Add shift') : 'Save changes'}
          </button>
        </>
      }
    >
      {conflict ? (
        <div
          className="mb-5 flex gap-2.5 rounded-card border p-3.5"
          style={{ background: 'var(--sp-warn-soft)', borderColor: 'var(--sp-warn-border)' }}
        >
          <CalendarOff size={17} aria-hidden="true" className="mt-0.5 shrink-0" style={{ color: 'var(--sp-warn-strong)' }} />
          <div className="text-[13px] leading-relaxed">
            <strong className="block text-ink">
              {conflict.name} marked {fmtDateLong(values.date)} as unavailable.
            </strong>
            {conflict.note ? <span className="block text-muted">Their note: {conflict.note}</span> : null}
            <span className="mt-1 block text-muted">
              You can still roster it. They will be notified when the week is published, the same as any other
              shift.
            </span>
          </div>
        </div>
      ) : null}

      {coverPanel}

      {error ? (
        <div
          className="mb-5 flex items-center gap-2 rounded-card border p-3 text-[13px] font-semibold"
          style={{ background: 'var(--sp-warn-soft)', borderColor: 'var(--sp-warn-border)', color: 'var(--sp-warn-strong)' }}
          role="alert"
        >
          <AlertTriangle size={15} aria-hidden="true" />
          {error}
        </div>
      ) : null}

      <Field label="Who" id="shift-user">
        <select
          id="shift-user"
          className="field"
          value={values.userId ?? ''}
          onChange={(e) => set('userId', e.target.value || null)}
        >
          <option value="">Open shift, nobody assigned yet</option>
          {staff
            .filter((s) => s.active !== false)
            .map((s) => (
              <option key={s.userId} value={s.userId}>
                {s.name}
              </option>
            ))}
        </select>
      </Field>

      <Field label="Date" id="shift-date">
        <input
          id="shift-date"
          className="field"
          type="date"
          value={values.date}
          onChange={(e) => set('date', e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Starts" id="shift-start" hint={fmtTime12(values.startTime)}>
          <input
            id="shift-start"
            className="field"
            type="time"
            step={300}
            value={values.startTime}
            aria-invalid={minutes <= 0 ? 'true' : undefined}
            onChange={(e) => set('startTime', e.target.value)}
          />
        </Field>
        <Field label="Finishes" id="shift-end" hint={fmtTime12(values.endTime)}>
          <input
            id="shift-end"
            className="field"
            type="time"
            step={300}
            value={values.endTime}
            aria-invalid={minutes <= 0 ? 'true' : undefined}
            onChange={(e) => set('endTime', e.target.value)}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Unpaid break" id="shift-break" hint="Minutes, taken off the paid total.">
          <input
            id="shift-break"
            className="field"
            type="number"
            min={0}
            max={240}
            step={5}
            value={values.breakMinutes}
            onChange={(e) => set('breakMinutes', Number(e.target.value) || 0)}
          />
        </Field>
        <Field label="Area" id="shift-area">
          <select
            id="shift-area"
            className="field"
            value={values.areaId ?? ''}
            onChange={(e) => set('areaId', e.target.value || null)}
          >
            <option value="">No area</option>
            {areas
              .filter((a) => a.active !== false)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </Field>
      </div>

      <Field label="Notes" id="shift-notes" hint="Anything the person should know before the shift.">
        <textarea
          id="shift-notes"
          className="field"
          rows={3}
          value={values.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Add a note for the team (optional)"
        />
      </Field>

      <div
        className="mb-4 flex items-center justify-between gap-3 rounded-card border p-3.5"
        style={{ background: 'var(--sp-surface-2)', borderColor: 'var(--sp-border)' }}
      >
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wide text-faint">This shift costs</div>
          <div className="tabular text-[20px] font-extrabold text-ink">
            {cost === null ? 'Not costed' : fmtMoney(cost)}
          </div>
          <div className="tabular text-[12px] text-muted">
            {fmtDuration(minutes)} paid
            {rate ? ` at ${fmtMoney(rate)} per hour, ${BUCKET_LABEL[bucket]?.toLowerCase()}` : ''}
          </div>
        </div>
        {!assignee ? (
          <span className="chip chip-neutral">Open shift</span>
        ) : (
          <span className="chip chip-accent">{BUCKET_LABEL[bucket]}</span>
        )}
      </div>

      <button
        type="button"
        className="btn w-full justify-start"
        disabled={values.locked && !isOwner}
        onClick={() => set('locked', !values.locked)}
        data-tip={
          values.locked
            ? 'Locked|A locked shift cannot be moved or edited by accident. Only the owner can unlock it.'
            : 'Lock this shift|Stops it being dragged or edited by accident once the week is settled.'
        }
      >
        {values.locked ? <Unlock size={15} aria-hidden="true" /> : <Lock size={15} aria-hidden="true" />}
        {values.locked ? 'Unlock this shift' : 'Lock this shift'}
      </button>

      {values.status === 'draft' && !isNew ? (
        <p className="mt-4 text-[12.5px] leading-relaxed text-faint">
          This shift is still a draft. Publish the week to send it to the team.
        </p>
      ) : null}
    </Drawer>
  );
}
